import React, { FC, useState, useEffect } from "react";
import {
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMutation } from "@apollo/client";
import { GET_AFFIDAVIT_SIGNING_URL } from "../../../../apolloClient/operation/mutations/mutation";
import { toast } from "react-toastify";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";

interface AffidavitProps {
  project: CustomerProjectDetails;
  onFetchProject: Function;
  permissions?: boolean;
}

export const Affidavit: FC<AffidavitProps> = ({
  project,
  onFetchProject,
  permissions,
}) => {
  const [dateOfCompletion, setDateOfCompletion] = useState("");
  const [scopeLineItemsCompleted, setScopeLineItemsCompleted] = useState("");
  const [rcvValue, setRcvValue] = useState("");
  const [county, setCounty] = useState("United States");
  const [selectedBrand, setSelectedBrand] = useState("glas");
  const [isGenerating, setIsGenerating] = useState(false);

  // Editable fields for auto-populated data
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [insuranceProvider, setInsuranceProvider] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [insuranceScopeDate, setInsuranceScopeDate] = useState("");

  const [getAffidavitSigningUrl] = useMutation(GET_AFFIDAVIT_SIGNING_URL);

  // Format number with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, "");
    
    // Split into integer and decimal parts
    const parts = numericValue.split(".");
    
    // Add commas to integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    
    // Return formatted value without $ prefix
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
  };

  const handleRcvValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Remove existing formatting
    const numericValue = input.replace(/[^\d.]/g, "");
    // Format and set
    if (numericValue) {
      setRcvValue(formatCurrency(numericValue));
    } else {
      setRcvValue("");
    }
  };

  // Initialize fields from project details
  useEffect(() => {
    if (project) {
      setClientName(
        `${project?.customerDetails?.firstName || ""} ${
          project?.customerDetails?.lastName || ""
        }`.trim()
      );
      setClientEmail(project?.customerDetails?.email || "");
      setClientPhone(project?.customerDetails?.phone || "");
      setPropertyAddress(
        project?.customerDetails?.address?.[0]
          ? `${project.customerDetails.address[0].street || ""}, ${
              project.customerDetails.address[0].city || ""
            }, ${project.customerDetails.address[0].state || ""} ${
              project.customerDetails.address[0].zipCode || ""
            }`.trim()
          : ""
      );
      setInsuranceProvider(
        project?.insuranceScope?.insuranceClaim?.companyName || ""
      );
      setClaimNumber(
        project?.insuranceScope?.insuranceClaim?.ClaimNumber || ""
      );
      setInsuranceScopeDate(
        project?.insuranceScope?.insuranceClaim?.ClaimDate || ""
      );
    }
  }, [project]);

  const handleGenerateAffidavit = async () => {
    // Validation
    if (!dateOfCompletion) {
      toast.error("Please enter date of completion");
      return;
    }

    if (!scopeLineItemsCompleted) {
      toast.error("Please enter scope line items completed");
      return;
    }

    if (!rcvValue) {
      toast.error("Please enter RCV value");
      return;
    }

    // Convert date from YYYY-MM-DD to MM-DD-YYYY
    const convertDateFormat = (dateString: string) => {
      const [year, month, day] = dateString.split("-");
      return `${month}-${day}-${year}`;
    };

    const formattedDate = convertDateFormat(dateOfCompletion);

    setIsGenerating(true);

    try {
      // Split line items by comma and trim whitespace
      const lineItems = scopeLineItemsCompleted
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      // Get insurance scope date from claim date
      const insuranceScopeDate = project?.insuranceScope?.insuranceClaim?.ClaimDate || "";

      const input = {
        projectId: project?.projectId,
        clientName: clientName,
        clientMailingAddress: propertyAddress,
        clientPhone: clientPhone,
        clientEmail: clientEmail,
        dateOfCompletion: formattedDate,
        county: county,
        propertyAddress: propertyAddress,
        insuranceProvider: insuranceProvider,
        claimNumber: claimNumber,
        insuranceScopeDate: insuranceScopeDate,
        scopeLineItemsCompleted: lineItems,
        rcvValue: rcvValue,
        source: selectedBrand,
      };

      const { data } = await getAffidavitSigningUrl({
        variables: { input },
      });

      if (data?.getAffidavitSigningUrl) {
        toast.success(
          "Affidavit signing email sent successfully! Please check your email."
        );

        // Refresh project data
        onFetchProject();

        // Clear form
        setDateOfCompletion("");
        setScopeLineItemsCompleted("");
        setRcvValue("");
      }
    } catch (error: any) {
      console.error("Error generating affidavit:", error);
      toast.error(
        error?.message || "Failed to generate affidavit. Please try again."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const inputStyle = {
    padding: "12px 14px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    width: "100%",
    backgroundColor: "#f9fafb",
    color: "#374151",
  };

  const disabledInputStyle = {
    ...inputStyle,
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    cursor: "not-allowed",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#374151",
  };

  return (
    <div style={{ width: "100%" }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Affidavit Generation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{ width: "100%" }}>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: "20px" }}>
              Fill in the required fields below to generate and send the
              affidavit for signing via DocuSign.
            </Typography>

            {/* Auto-populated fields (read-only) */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>Client Name</label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>Client Email</label>
                <input
                  type="text"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>Client Phone</label>
                <input
                  type="text"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>Property Address</label>
                <input
                  type="text"
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>Insurance Provider</label>
                <input
                  type="text"
                  value={insuranceProvider}
                  onChange={(e) => setInsuranceProvider(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>Claim Number</label>
                <input
                  type="text"
                  value={claimNumber}
                  onChange={(e) => setClaimNumber(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>Insurance Scope Date</label>
                <input
                  type="text"
                  value={insuranceScopeDate}
                  onChange={(e) => setInsuranceScopeDate(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "30px", marginBottom: "16px" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Required Fields
              </Typography>
            </div>

            {/* User input fields */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>
                  Date of Completion <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="date"
                  value={dateOfCompletion}
                  onChange={(e) => setDateOfCompletion(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
                <span style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px", display: "block" }}>
                  Format: MM-DD-YYYY
                </span>
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>County</label>
                <input
                  type="text"
                  placeholder="Enter county"
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
                <span style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px", display: "block" }}>
                  Optional - auto-filled if available
                </span>
              </div>
              <div style={{ flex: "1 1 100%", minWidth: "250px" }}>
                <label style={labelStyle}>
                  Scope Line Items Completed <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  placeholder="e.g., 1-5, 8, 12, 15-20"
                  value={scopeLineItemsCompleted}
                  onChange={(e) => setScopeLineItemsCompleted(e.target.value)}
                  disabled={!permissions || isGenerating}
                  rows={2}
                  style={{
                    ...(!permissions || isGenerating ? disabledInputStyle : inputStyle),
                    resize: "vertical",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px", display: "block" }}>
                  Enter line items separated by commas (e.g., 1-5, 8, 12, 15-20)
                </span>
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>
                  RCV Value <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., $10,000"
                  value={rcvValue}
                  onChange={handleRcvValueChange}
                  disabled={!permissions || isGenerating}
                  style={!permissions || isGenerating ? disabledInputStyle : inputStyle}
                />
              </div>
              <div style={{ flex: "1 1 calc(50% - 8px)", minWidth: "250px" }}>
                <label style={labelStyle}>
                  Brand Styling <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  disabled={!permissions || isGenerating}
                  style={{
                    ...(!permissions || isGenerating ? disabledInputStyle : inputStyle),
                    height: "44px",
                    backgroundColor: !permissions || isGenerating ? "#f3f4f6" : "#f9fafb",
                    cursor: !permissions || isGenerating ? "not-allowed" : "pointer",
                    outline: "none",
                  }}
                >
                  <option value="glas">Glas</option>
                  <option value="waybe">Waybe</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateAffidavit}
                disabled={!permissions || isGenerating}
                startIcon={
                  isGenerating ? <CircularProgress size={20} /> : null
                }
                sx={{
                  textTransform: "none",
                  padding: "10px 30px",
                  fontSize: "16px",
                }}
              >
                {isGenerating ? "Generating..." : "Generate Affidavit"}
              </Button>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
